import React, { useState, useEffect, useMemo } from "react";
import {
	Search,
	List,
	Edit2,
	Check,
	X,
	Trash2,
	AlertTriangle,
	Plus,
	HelpCircle,
	Upload,
	Download,
} from "lucide-react";
import { toast } from "react-toastify";
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	InputGroup,
	InputGroupText,
	Pagination,
	PaginationItem,
	PaginationLink,
	Row,
	Col,
} from "reactstrap";
import ExportImport from "./ExportImport";
import AddMultipleSynonyms from "./AddMultipleSynonyms";
import "./GeneralTab.scss";
import { DownloadData, PostData } from "../../services/FetchData";
import "@fortawesome/fontawesome-free/css/all.min.css";
import DetailSkeleton from "../Skeleton/DetailSkeleton";

const GeneralTab = ({ currentWebsiteId }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [data, setData] = useState([]);
	const [selectedIds, setSelectedIds] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [showAddMultiple, setShowAddMultiple] = useState(false);
	const [sortConfig, setSortConfig] = useState({
		key: "searchTerm",
		direction: "asc",
	});
	const [itemsPerPage, setItemsPerPage] = useState(20);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showAddModal, setShowAddModal] = useState(false);
	const [showImportModal, setShowImportModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editingItem, setEditingItem] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [exporting, setexporting] = useState(false);

	const [pagination, setPagination] = useState({
		page: 1,
		perPage: 5,
		total: 0,
		totalPages: 1,
	});

	// Debounced search: fetch synonyms after user stops typing
	useEffect(() => {
		const handler = setTimeout(() => {
			setPagination((prev) => ({
				...prev,
				page: 1, // Reset to first page when searching
			}));
			// Note: fetchSynonyms uses current searchTerm and pagination state
			fetchSynonyms();
		}, 500);

		return () => {
			clearTimeout(handler);
		};
		// Only run this effect when searchTerm or currentWebsiteId changes
		// Note: pagination is reset to page 1 above but changes to perPage will still require a manual reload
	}, [searchTerm, currentWebsiteId]);

	const fetchSynonyms = () => {
		console.log("fetchSynonyms called with:", {
			websiteId: currentWebsiteId,
			page: pagination.page,
			perPage: pagination.perPage,
			searchTerm: searchTerm,
		});

		if (!currentWebsiteId) {
			console.warn("No website ID provided, skipping fetch");
			setData([]);
			return;
		}

		setIsLoading(true);
		setError(null);

		PostData("ms1", "api/v1/synonyms/getAllSynonyms", {
			websiteId: currentWebsiteId,
			page: pagination.page,
			perPage: pagination.perPage,
			searchTerm: searchTerm,
		})
			.then((result) => {
				console.log("API Response:", result);
				if (result && result.status === "success" && result.data) {
					const responseData = result.data.data || [];
					const paginationData = result.data.pagination || {
						page: 1,
						perPage: 5,
						total: 0,
						totalPages: 1,
					};

					const formattedData = responseData.map((synonym) => ({
						id: synonym.id || synonym.root,
						searchTerm: synonym.root || "",
						synonyms: Array.isArray(synonym.synonyms) ? synonym.synonyms : [],
					}));

					setPagination({
						page: parseInt(paginationData.page),
						perPage: parseInt(paginationData.perPage),
						total: parseInt(paginationData.total),
						totalPages: parseInt(paginationData.totalPages),
					});

					setData(formattedData);

					if (formattedData.length === 0) {
						console.log("No synonyms found for website:", currentWebsiteId);
						setError(
							"No synonyms found. Add your first synonym using the 'Add Multiple' button."
						);
					} else {
						setError(null);
					}
				} else {
					console.log("No data or unsuccessful response:", result);
					setData([]);
					setError(
						"No synonyms found. Add your first synonym using the 'Add Multiple' button."
					);
				}
			})
			.catch((error) => {
				console.error("Error fetching synonyms:", error);
				setError("Failed to load synonyms. Please try again later.");
				setData([]);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleSaveNewEntries = (validEntries) => {
		if (validEntries.length > 0) {
			handleSaveSynonyms(validEntries);
			setShowAddModal(false);
		}
	};

	const handleSaveSynonyms = (entriesToSave) => {
		setIsLoading(true);
		const formattedEntries = entriesToSave.map((entry) => ({
			id: entry.id || Date.now().toString(),
			searchTerm: entry.searchTerm,
			synonyms: Array.isArray(entry.synonyms)
				? entry.synonyms
				: [entry.synonyms].filter(Boolean),
		}));

		PostData("ms1", "api/v1/synonyms/createOrUpdateSynonym", {
			websiteId: currentWebsiteId,
			synonyms: formattedEntries,
		}).then((result) => {
			if( result !== "Invalid" && result !== "undefined" ){
				if (result.status === "success") {
					fetchSynonyms();
				}
			}
		}).catch((error) => {
			console.error("Error saving synonyms:", error);
		}).finally(() => {
			setIsLoading(false);
		});
	};

	const handleEditClick = (item) => {
		setEditingItem({
			id: item.id,
			searchTerm: item.searchTerm,
			synonyms: item.synonyms.join(", "),
			originalSearchTerm: item.searchTerm,
			originalSynonyms: item.synonyms.join(", "),
		});
		setShowEditModal(true);
	};

	const handleDeleteSelected = () => {
		if (selectedIds.length === 0) return;
		setShowDeleteConfirm(true);
	};

	const confirmDelete = () => {
		setShowDeleteConfirm(false);
		setIsLoading(true);

		PostData("ms1", "api/v1/synonyms/deleteSynonym", {
			websiteId: currentWebsiteId,
			synonymIds: selectedIds,
		})
			.then((result) => {
				if (result && result.status === "success") {
					setSelectedIds([]);
					fetchSynonyms();
				}
				setIsLoading(false);
			})
			.catch((error) => {
				console.error("Error deleting synonyms:", error);
				setError("Failed to delete synonyms");
				setIsLoading(false);
			});
	};

	const cancelDelete = () => {
		setShowDeleteConfirm(false);
	};

	useEffect(() => {
		if (currentWebsiteId) {
			fetchSynonyms();
		}
	}, [currentWebsiteId, pagination.page, pagination.perPage, refreshKey]);

	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= pagination.totalPages) {
			setPagination((prev) => ({
				...prev,
				page: newPage,
			}));
		}
	};

	const handleSearch = (e) => {
		e.preventDefault();

		setPagination((prev) => ({
			...prev,
			page: 1,
		}));

		fetchSynonyms();
	};

	const filteredData = useMemo(() => {
		if (!data) return [];

		let result = [...data];

		if (searchTerm) {
			const term = searchTerm.toLowerCase();
			result = result.filter(
				(item) =>
					(item.searchTerm?.toLowerCase() || "").includes(term) ||
					(item.synonyms || []).some((syn) =>
						(syn || "").toLowerCase().includes(term)
					)
			);
		}

		if (sortConfig.key) {
			result.sort((a, b) => {
				const aValue = a[sortConfig.key] || "";
				const bValue = b[sortConfig.key] || "";

				if (aValue < bValue) {
					return sortConfig.direction === "asc" ? -1 : 1;
				}
				if (a[sortConfig.key] > b[sortConfig.key]) {
					return sortConfig.direction === "asc" ? 1 : -1;
				}
				return 0;
			});
		}

		return result;
	}, [data, searchTerm, sortConfig]);

	const requestSort = (key) => {
		let direction = "asc";
		if (sortConfig.key === key && sortConfig.direction === "asc") {
			direction = "desc";
		}
		setSortConfig({ key, direction });
	};

	const toggleSelectAll = (e) => {
		if (e.target.checked) {
			setSelectedIds(filteredData.map((item) => item.id));
		} else {
			setSelectedIds([]);
		}
	};

	const toggleSelect = (id) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
		);
	};

	const handleExport = async () => {
		try {
			setexporting(true);
			const response = await DownloadData("ms1", "api/v1/synonyms/exportSynonyms", {
				websiteId: currentWebsiteId,
				responseType: "blob",
				headers: { Accept: "text/csv" },
			});

			const blob = new Blob([response], { type: "text/csv;charset=utf-8" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `synonyms_${new Date().toISOString().split("T")[0]}.csv`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			toast.success("Export completed successfully!");
			setexporting(false);
		} catch (error) {
			console.error("Export failed:", error);
			toast.error(error.message || "Failed to export synonyms");
			setexporting(false);
		}
	};

	return (
		<div className="general-tab">
			<div className="controls">
				<form onSubmit={handleSearch} className="search-form">
					<InputGroup>
						<Input
							type="text"
							placeholder="Search synonyms..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyUp={(e) => e.key === "Enter" && handleSearch(e)}
							className="form-control"
						/>
						{/* <Button
							type="button"
							color="light"
							onClick={() => {
								setSearchTerm("");
								setPagination((prev) => ({
									...prev,
									page: 1,
									perPage: prev.perPage,
								}));

								setRefreshKey((prev) => prev + 1);
							}}
							title="Refresh"
							style={{
								border: "1px solid #ced4da",
								borderLeft: "none",
								borderRight: "none",
								backgroundColor: "white",
							}}
						>
							<i className="fas fa-sync-alt"></i>
						</Button>
						<Button
							type="submit"
							color="primary"
							disabled={searchTerm.length === 0}
							style={{
								borderTopLeftRadius: 0,
								borderBottomLeftRadius: 0,
							}}
						>
							<i className="fas fa-search me-1"></i>
							Search
						</Button> */}
					</InputGroup>
				</form>

				<div className="actions" style={{ display: "flex", gap: "0.5rem" }}>
					<Button
						color="light"
						onClick={() => setShowImportModal(true)}
						disabled={isLoading}
						className="d-flex align-items-center gap-2"
						style={{
							fontWeight: 500,
							fontSize: "0.875rem",
							padding: "0.5rem 1rem",
							border: "1px solid #ddd",
						}}
					>
						<Upload size={16} /> Import
					</Button>

					<Button
						color="light"
						onClick={handleExport}
						disabled={isLoading || data.length === 0}
						className="d-flex align-items-center gap-2"
						style={{
							fontWeight: 500,
							fontSize: "0.875rem",
							padding: "0.5rem 1rem",
							border: "1px solid #ddd",
						}}
					>
						<Download size={16} />
						{exporting ? "Exporting..." : "Export"}
					</Button>

					<Button
						color="primary"
						onClick={() => setShowAddModal(true)}
						disabled={isLoading}
						className="d-flex align-items-center gap-2"
						style={{
							fontWeight: 500,
							fontSize: "0.875rem",
							padding: "0.5rem 1rem",
							backgroundColor: "#3c64f4",
						}}
					>
						<Plus size={16} /> Synonyms
					</Button>

					{selectedIds.length > 0 && (
						<button
							className="btn btn-danger"
							disabled={isLoading}
							onClick={handleDeleteSelected}
							style={{
								backgroundColor: "#ef4444",
								color: "white",
								border: "1px solid #ef4444",
								display: "inline-flex",
								alignItems: "center",
								gap: "0.5rem",
								padding: "0.5rem 1rem",
								borderRadius: "4px",
								cursor: isLoading ? "not-allowed" : "pointer",
								transition: "all 0.2s",
								opacity: isLoading ? 0.6 : 1,
							}}
						>
							<Trash2 size={16} /> ({selectedIds.length})
						</button>
					)}
				</div>

				<Modal isOpen={showDeleteConfirm} toggle={cancelDelete} centered>
					<ModalHeader toggle={cancelDelete}>
						<div className="d-flex align-items-center">
							<AlertTriangle size={20} className="text-warning me-2" />
							Delete {selectedIds.length}{" "}
							{selectedIds.length === 1 ? "Entry" : "Entries"}
						</div>
					</ModalHeader>
					<ModalBody>
						<p>
							Are you sure you want to delete the selected{" "}
							{selectedIds.length === 1 ? "entry" : "entries"}? This action
							cannot be undone.
						</p>
					</ModalBody>
					<ModalFooter>
						<Button
							color="secondary"
							onClick={cancelDelete}
							disabled={isLoading}
						>
							Cancel
						</Button>
						<Button color="danger" onClick={confirmDelete} disabled={isLoading}>
							{isLoading
								? "Deleting..."
								: `Delete ${selectedIds.length} ${selectedIds.length === 1 ? "Item" : "Items"
								}`}
						</Button>
					</ModalFooter>
				</Modal>

				<Modal
					isOpen={showImportModal}
					toggle={() => setShowImportModal(false)}
					size="lg"
					centered
					className="import-modal"
				>
					<ModalHeader toggle={() => setShowImportModal(false)}>
						Import Synonyms
					</ModalHeader>
					<ModalBody style={{ padding: 0 }}>
						<ExportImport
							key={refreshKey}
							onClose={() => {
								setShowImportModal(false);
								setRefreshKey((prev) => prev + 1);
								fetchSynonyms();
							}}
						/>
					</ModalBody>
				</Modal>

				<Modal
					isOpen={showAddModal}
					toggle={() => setShowAddModal(false)}
					size="lg"
					centered
				>
					<ModalHeader toggle={() => setShowAddModal(false)}>
						<div className="d-flex align-items-center">Add Synonyms</div>
					</ModalHeader>
					<ModalBody style={{ padding: 0 }}>
						<AddMultipleSynonyms
							onSave={handleSaveNewEntries}
							onClose={() => setShowAddModal(false)}
							websiteId={currentWebsiteId}
						/>
					</ModalBody>
				</Modal>

				<Modal
					isOpen={showEditModal}
					toggle={() => setShowEditModal(false)}
					size="lg"
					centered
				>
					<ModalHeader toggle={() => setShowEditModal(false)}>
						<div className="d-flex align-items-center">Edit Synonyms</div>
					</ModalHeader>
					<ModalBody style={{ padding: 0 }}>
						{editingItem && (
							<AddMultipleSynonyms
								isEditMode={true}
								initialEntry={{
									...editingItem,
									synonyms: Array.isArray(editingItem.synonyms)
										? editingItem.synonyms.join(", ")
										: editingItem.synonyms || "",
								}}
								onSave={(data) => {
									const updatedItem = {
										...editingItem,
										searchTerm: data.searchTerm,
										synonyms: Array.isArray(data.synonyms)
											? data.synonyms
											: data.synonyms
												.split(",")
												.map((s) => s.trim())
												.filter(Boolean),
									};

									handleSaveSynonyms([updatedItem]);
									setShowEditModal(false);
									setEditingItem(null);
								}}
								onClose={() => {
									setShowEditModal(false);
									setEditingItem(null);
								}}
								websiteId={currentWebsiteId}
							/>
						)}
					</ModalBody>
				</Modal>
			</div>

			<div className="table-container">
				<table className="synonyms-table">
					<thead>
						<tr>
							<th>
								<input
									type="checkbox"
									checked={
										selectedIds.length === filteredData.length &&
										filteredData.length > 0
									}
									onChange={toggleSelectAll}
									disabled={isLoading}
								/>
							</th>
							<th
								className={`sortable ${sortConfig.key === "searchTerm" ? sortConfig.direction : ""
									}`}
								onClick={() => requestSort("searchTerm")}
							>
								Search term
								<span className="sort-arrow">
									{sortConfig.key === "searchTerm" &&
										(sortConfig.direction === "asc" ? "↑" : "↓")}
								</span>
							</th>
							<th>Synonyms</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={4}>
									<DetailSkeleton />
								</td>
							</tr>
						) : (
							<>
								{data.map((item) => (
									<tr
										key={item.id}
										className={selectedIds.includes(item.id) ? "selected" : ""}
									>
										<td>
											<input
												type="checkbox"
												checked={selectedIds.includes(item.id)}
												onChange={() => toggleSelect(item.id)}
												disabled={isLoading}
											/>
										</td>
										<td>{item.searchTerm}</td>
										<td>{item.synonyms.join(", ")}</td>
										<td className="actions">
											<button
												className="btn-icon"
												onClick={() => handleEditClick(item)}
												disabled={isLoading}
												title="Edit"
											>
												<Edit2 size={16} />
											</button>
										</td>
									</tr>
								))}
							</>
						)}
					</tbody>
				</table>
			</div>

			{data.length === 0 && !isLoading && (
				<div className="no-results">
					{error ||
						"No synonyms found. Try adjusting your search or add new synonyms."}
				</div>
			)}

			{pagination.totalPages > 1 && (
				<div className="d-flex justify-content-between align-items-center mt-3">
					<div className="d-flex align-items-center">
						<span className="me-2">Show:</span>
						<Input
							type="select"
							bsSize="sm"
							style={{ width: "auto" }}
							value={pagination.perPage}
							onChange={(e) => {
								setPagination((prev) => ({
									...prev,
									perPage: Number(e.target.value),
									page: 1,
								}));
							}}
						>
							<option value={5}>5</option>
							<option value={10}>10</option>
							<option value={20}>20</option>
						</Input>
						<span className="ms-3">
							Showing {data.length} of {pagination.total} items (Page{" "}
							{pagination.page} of {pagination.totalPages})
						</span>
					</div>
					<Pagination>
						<PaginationItem disabled={pagination.page === 1}>
							<PaginationLink
								previous
								onClick={() => handlePageChange(pagination.page - 1)}
							>
								&laquo;
							</PaginationLink>
						</PaginationItem>

						{Array.from(
							{ length: Math.min(5, pagination.totalPages) },
							(_, i) => {
								let pageNum;
								if (pagination.totalPages <= 5) {
									pageNum = i + 1;
								} else if (pagination.page <= 3) {
									pageNum = i + 1;
								} else if (pagination.page >= pagination.totalPages - 2) {
									pageNum = pagination.totalPages - 4 + i;
								} else {
									pageNum = pagination.page - 2 + i;
								}

								return (
									<PaginationItem
										key={pageNum}
										active={pagination.page === pageNum}
									>
										<PaginationLink
											onClick={() => handlePageChange(pageNum)}
											style={
												pagination.page === pageNum ? { color: "white" } : {}
											}
										>
											{pageNum}
										</PaginationLink>
									</PaginationItem>
								);
							}
						)}

						<PaginationItem
							disabled={pagination.page === pagination.totalPages}
						>
							<PaginationLink
								next
								onClick={() => handlePageChange(pagination.page + 1)}
							>
								&raquo;
							</PaginationLink>
						</PaginationItem>
					</Pagination>
				</div>
			)}
		</div>
	);
};

export default GeneralTab;
